import React, { useEffect, useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import Logo from '../assets/MALogo.svg'
import { useNavigate } from 'react-router-dom';
import Confirmation from '../components/contribution/Confirmation';
import ContributionPageError from '../components/errorPages/ContributionPageError';
import PageLoader from "../components/loaders/PageLoader";
import { formatCharacterName } from '../utils';
import ToastNotification from '../components/ToastNotification';
import { getContributionCode } from '../utils';

const Contributions = () => {

    const [user, setUser] = useState(null); // User state
    const [amount, setAmount] = useState('0.00'); // Default support amount
    const [paymentSuccess, setPaymentSuccess] = useState(false); // State to track if payment was successful
    const [character, setCharacter] = useState("general_support"); // State to track selected character
    const [selectedOther, setSelectedOther] = useState(false); // State for other character selection
    const [isLoading, setIsLoading] = useState(true); // State to track loading status
    const [serverMessage, setServerMessage] = useState(null); // State to track server messages
    const [authResponse, setAuthResponse] = useState(null); // State to track authentication response
    const [authDescription, setAuthDescription] = useState(''); // State to track authentication description/message
    const [paymentErrorLocation, setPaymentErrorLocation] = useState(null); // State to track where payment error occurred

    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    const handleCharacterSelect = (char) => {
        
        if (char !== 'other_character') {
            setCharacter(char);
            setSelectedOther(false);
        } else {
            setCharacter('ice_climbers'); // Reset character when "other" is selected
            setSelectedOther(true);
        }
    };

    const otherCharacters = [
        "ice_climbers", //'Ice Climbers'
        "samus", //'Samus'
        "yoshi", //'Yoshi'
        "pikachu", //'Pikachu'
        "luigi", //'Luigi'
        "dr_mario", //'Dr. Mario'
        "donkey_kong", //'Donkey Kong'
        "ganondorf", //'Ganondorf'
        "mario", //'Mario'
        "link", //'Link'
        "young_link", //'Young Link'
        "ness", //'Ness'
        "zelda", //'Zelda'
        "mewtwo", //'Mewtwo'
        "roy", //'Roy'
        "mr_game_and_watch", //'Mr. Game & Watch'
        "pichu", //'Pichu'
        "kirby", //'Kirby'
        "bowser", //'Bowser'
    ];

    const isPayPalButtonDisabled = parseFloat(amount) < 1 || amount === '' || !character; // Disable PayPal button if amount is less than $1 or no character is selected
    const navigate = useNavigate();

    // getUserData() - fetches user data and updates the user state BUT must accept if the user is not logged in
    const getUserData = async () => {
        try {
            const response = await fetch(`${apiUrl}/user/getuser/`, {
                method: "GET",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
            });

            const data = await response.json();
            const userData = data.data;

            if (userData && !userData.email_verified) {
                setAuthResponse(406); // User's email is not verified
                setAuthDescription("Please verify your email to make a contribution.");
                setIsLoading(false);
            } else if (userData && userData.email_verified) {
                setUser(userData);
                setAuthResponse(200); // User is authenticated
                setIsLoading(false);
            } else {
                setAuthResponse(401); // User is not authenticated
                setAuthDescription("You are not logged in. Please sign in to make a contribution.");
                setIsLoading(false);
            }

        } catch (error) {
            console.error("Error fetching user's information:", error);
        }
    }    

    // handleCreateOrder() - creates an order and returns the order ID
    const handleOrderCreate = async (data, actions) => {

        // Create a one-time payment order
        return actions.order.create({

            // custom_id: custom_id,
            application_context: {
                shipping_preference: "NO_SHIPPING", // No shipping address needed for digital goods
            },
            purchase_units: [
                {
                    amount: {
                        value: amount, // Dynamically set the amount based on the selected plan
                    },
                    description: `Melee Academy Support - ${formatCharacterName(character)}`, // Description based on the selected plan
                    custom_id: `subtype=${getContributionCode(character)}&userId=${user.userId}`, // Custom data to identify user and subscription type
                },
            ],
        });
    }

    useEffect(() => {
        getUserData();
    }, []);

    // handleApprove() - handles the approval of the order
    const handleOnApprove = async (data, actions) => {
        try {
            const order = await actions.order.capture();
            // To be used later for invoices 
            // NOTE: edit the same invoice from subscriptions and the backend controllers since it doesn't capture all of these ids yet
            const captureId = order.purchase_units[0].payments.captures[0].id;
            const orderId = data.orderID;

            await createInvoice(orderId, captureId);

            // Moved these actions to webhook instead
            // await recordContribution(orderId);
            // await updateUserAttributes(); // Update user attributes after successful payment and contribution recording (contributorType and contributedList)

            setPaymentSuccess(true);

        } catch (error) {
            console.error("Error during payment approval:", error);
            
            if (paymentErrorLocation === "createInvoice") {
                setAuthDescription("Your contribution payment was successful, and we're preparing your invoice for your records. Our system will automatically retry to create your invoice and finalize the details. You can check your account page for updates. If the invoice isn't available within 72 hours, please contact support.");
                setAuthResponse(500);
            } else {
                setAuthDescription("We encountered a temporary issue while processing your contribution. Don't worry—our system will automatically retry and finalize everything. You can check the status of your contribution and invoices on your account page. If the issue isn't resolved within 72 hours, please contact support for further assistance.");
                setAuthResponse(500);
            }
        }
    };

    const recordContribution = async (orderId) => {

        const item = {
            orderId: orderId,
            userId: user.userId,
            characterId: character,
            amount: amount, // Amount as a string and will be converted to number in the backend
        };

        try {
            const response = await fetch(`${apiUrl}/contribution/record-contribution`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(item)
            });

            const data = await response.json();

            if (!data.success) {
                setPaymentErrorLocation("recordContribution");
                throw new Error(data.message);
            }
        } catch (error) {
            console.error("Error recording contribution:", error);
            setPaymentErrorLocation("recordContribution");
            throw new Error("Error recording contribution");
            // Use an error location state so handleOnApprove knows what to show the user
        }
    };

    const updateUserAttributes = async () => {
        const updateDetails = {
            userId: user.userId,
            character: character,
        }

        try {
            const response = await fetch(`${apiUrl}/contribution/update-contributor-attributes`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updateDetails)
            });
    
            const data = await response.json();

            if (!data.success) {
                console.error("Warning: Could not update user attributes after contribution.");
                setPaymentErrorLocation("updateUserAttributes");
                throw new Error(data.message);
            }
        } catch (error) {
            console.error("Error updating user attributes:", error);
            setPaymentErrorLocation("updateUserAttributes");
            throw new Error("Error updating user attributes");
        }

    };


    // createInvoice() - creates an invoice for the user ONLY if they are logged in
    const createInvoice = async (orderId, captureId) => {
        const invoiceDetails = {
            invoiceId : orderId, // Use the subscriptionId or orderId as the invoiceId
            captureId: captureId, // PayPal resource ID for reference
            createdDate: new Date().toISOString().split('T')[0],
            expirationDate: "N/A", // No expiration date for contributions
            paymentAmount: amount,
            paymentCurrency: "USD",
            paymentMethod: "paypal",
            paymentStatus: "PAID",
            purchaseType: getContributionCode(character),
            userId: user.userId,
        }

        try {
            const response = await fetch(`${apiUrl}/contribution/create-contribution-invoice`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(invoiceDetails)
            });
    
            const data = await response.json();
    
            if (data.success) {
                console.log("Invoice created successfully.");
            }
            else {
                setPaymentErrorLocation("createInvoice");
                throw new Error("Invoice creation failed");
            }
        } catch (error) {
            setPaymentErrorLocation("createInvoice");
            throw new Error("Error creating invoice");
        }
    }

    const sendInvoiceEmail = async (orderId) => {

        const emailDetails = {
            email: user.email,
            price: amount + " USD",
            contributionType: formatCharacterName(character),
        }

        try {
            const response = await fetch(`${apiUrl}/contribution/send-contribution-invoice-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(emailDetails)
            });

            const data = await response.json();

            if (!data.success) {
                setPaymentErrorLocation("sendInvoiceEmail");
                throw new Error("Invoice email sending failed");
            }
        } catch (error) {
            setPaymentErrorLocation("sendInvoiceEmail");
            throw new Error("Error sending invoice email");
        }
    }

    return (
        authResponse === 401 || authResponse === 404 || authResponse === 406 || authResponse === 500 ? (
            <ContributionPageError status={authResponse} message={authDescription} />
        ) : authResponse === 200 ? (

            <PayPalScriptProvider
                options={{
                    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                    "vault": true,
                    "intent": "capture",
                }}
            >
                <div className="wrapper">
                    <div className="d-flex justify-content-center" style={{paddingTop: "40px", cursor: "pointer"}} onClick={() => navigate('/')}>
                        <img src={Logo} alt="Melee Academy Logo" style={{width: "50px", marginRight: "12px"}}/>
                        <h2 style={{fontWeight: 600}}>Melee Academy</h2>
                    </div>
                    <div className='background-grid d-flex flex-column justify-content-center align-items-center' style={{minHeight: "80vh"}}>
                        {paymentSuccess ? (
                            <Confirmation amount={amount} character={character} />
                        ) : (
                            // <div className="container mt-5">
                                <div className="d-flex flex-column align-items-center mt-4">
                                    <div className="rounded p-5 d-flex flex-column justify-content-center align-items-center contribution-container">
                                        <h4 className="text-center mb-3">Support Melee Academy</h4>
                                        {/* <p className="mb-3 text-center">Your contribution helps support Melee Academy and future platform improvements.</p> */}

                                        <p className="mb-2">Choose an amount to contribute:</p>
                                        <div className="d-flex justify-content-center mb-3">
                                            <button className='btn' style={{backgroundColor: amount === '5.00' ? "#FFD700" : "#1C1F2A", color: amount === '5.00' ? 'black' : 'white', fontWeight: amount === '5.00' ? 'bold' : 'normal', width: "70px", margin: "0 5px", fontSize: "14px"}} onClick={() => setAmount('5.00')}>$5</button>
                                            <button className='btn' style={{backgroundColor: amount === '10.00' ? "#FFD700" : "#1C1F2A", color: amount === '10.00' ? 'black' : 'white', fontWeight: amount === '10.00' ? 'bold' : 'normal', width: "70px", margin: "0 5px", fontSize: "14px"}} onClick={() => setAmount('10.00')}>$10</button>
                                            <button className='btn' style={{backgroundColor: amount === '20.00' ? "#FFD700" : "#1C1F2A", color: amount === '20.00' ? 'black' : 'white', fontWeight: amount === '20.00' ? 'bold' : 'normal', width: "70px", margin: "0 5px", fontSize: "14px"}} onClick={() => setAmount('20.00')}>$20</button>
                                            <button className='btn' style={{backgroundColor: amount === '50.00' ? "#FFD700" : "#1C1F2A", color: amount === '50.00' ? 'black' : 'white', fontWeight: amount === '50.00' ? 'bold' : 'normal', width: "70px", margin: "0 5px", fontSize: "14px"}} onClick={() => setAmount('50.00')}>$50</button>
                                        </div>
                                        <div className="d-flex align-items-center mb-4">
                                            <span style={{ color: "white", fontSize: "18px", marginRight: "5px" }}>$</span>
                                            <input 
                                                type="number" 
                                                min="1" 
                                                value={amount} 
                                                id="amount" 
                                                placeholder='Custom Amount' 
                                                className="form-control" 
                                                style={{ width: "150px", backgroundColor: "#1C1F2A", border: "1px solid #E5E5E5", color: "white", MozAppearance: "textfield" }} 
                                                onChange={(e) => {
                                                    setAmount(e.target.value); // Allow free editing without strict formatting
                                                }} 
                                                onBlur={(e) => {
                                                    const value = parseFloat(e.target.value).toFixed(2); // Format to two decimal places on blur
                                                    setAmount(isNaN(value) ? '0.00' : value); // Handle invalid input
                                                }} 
                                                onWheel={(e) => e.target.blur()} // Prevent scrolling behavior

                                            />
                                        </div>
                                        <div style={{ width: "300px" }}>
                                            <PayPalButtons
                                                style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 45 }}
                                                disabled={isPayPalButtonDisabled}
                                                createOrder={handleOrderCreate}
                                                onApprove={handleOnApprove}
                                                onCancel={(data) => {
                                                    // Handle payment cancellation
                                                    console.log("Payment cancelled");
                                                    setServerMessage({ success: false, message: "Payment was cancelled. If this was a mistake, please try again." });
                                                }}
                                                onError={(err) => {
                                                    // Handle errors during the payment process
                                                    console.error("PayPal Button Error:", err);
                                                    setServerMessage({ success: false, message: "There was an error processing your payment. Please try again." });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            // </div>
                        )}
                    </div>
                </div>
                <ToastNotification
                    message={serverMessage}
                    setServerMessage={setServerMessage}
                />
            </PayPalScriptProvider>
        ) : (
            <PageLoader isLoading={isLoading} />
        )
    );
}

export default Contributions;
